import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@rsos/base-components';
import {
  PopoverContainer,
  NoteTextArea,
  SaveButton,
  FieldContainer,
  CustomSelect,
  SelectHeader,
  SelectDropdown,
  SelectOption,
  ColorBox,
  CloseButton,
  HeaderRow,
} from './NotePopover.styles';
import {
  COLOR_OPTIONS,
  getSelectedColor,
  handleColorSelect,
  handleDropdownToggle,
  ColorType,
  DropdownType,
} from './helpers';

interface NotePopoverProps {
  onClose: () => void;
  onSubmit: (note: string, boundaryColor: string, polygonColor: string) => void;
  note: string;
  boundaryColor: string;
  polygonColor: string;
  onNoteChange: (type: string, value: string) => void;
}

const NotePopover: React.FC<NotePopoverProps> = ({
  onClose,
  onSubmit,
  note,
  boundaryColor,
  polygonColor,
  onNoteChange,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [boundaryOpen, setBoundaryOpen] = useState<boolean>(false);
  const [polygonOpen, setPolygonOpen] = useState<boolean>(false);

  useEffect(() => {
    const focusTextarea = (): void => {
      if (textareaRef.current) {
        const container = textareaRef.current.closest('[data-popover]');
        if (container) {
          (container as HTMLElement).focus();
        }
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    };

    const timeoutId = setTimeout(focusTextarea, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleSubmit = (): void => {
    onSubmit(note, boundaryColor, polygonColor);
    onClose();
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleContainerClick = (): void => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const onColorSelect = handleColorSelect(
    onNoteChange,
    setBoundaryOpen,
    setPolygonOpen,
  );
  const onDropdownToggle = handleDropdownToggle(
    setBoundaryOpen,
    setPolygonOpen,
  );

  const renderColorSelector = (
    type: DropdownType,
    currentColor: string,
    isOpen: boolean,
  ): JSX.Element => (
    <FieldContainer>
      <CustomSelect>
        <SelectHeader
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            onDropdownToggle(type);
          }}
        >
          <ColorBox color={getSelectedColor(currentColor).color} />
          <span>{type === 'boundary' ? 'Border' : 'Fill'}</span>
        </SelectHeader>
        {isOpen && (
          <SelectDropdown>
            {COLOR_OPTIONS.map(option => (
              <SelectOption
                key={option.value}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  onColorSelect(
                    (type === 'boundary'
                      ? 'boundaryColor'
                      : 'polygonColor') as ColorType,
                    option.value,
                  );
                }}
              >
                <ColorBox color={option.color} />
              </SelectOption>
            ))}
          </SelectDropdown>
        )}
      </CustomSelect>
    </FieldContainer>
  );

  return (
    <PopoverContainer data-popover tabIndex={-1} onClick={handleContainerClick}>
      <HeaderRow>
        <CloseButton onClick={handleSubmit}>×</CloseButton>
      </HeaderRow>

      <Box display="flex" horizontal justify="flex-start" mb={2}>
        {renderColorSelector('boundary', boundaryColor, boundaryOpen)}
        {renderColorSelector('polygon', polygonColor, polygonOpen)}
      </Box>

      <FieldContainer mb={2}>
        <NoteTextArea
          ref={textareaRef}
          rows={3}
          value={note}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onNoteChange('note', e.target.value)
          }
          onKeyPress={handleKeyPress}
          placeholder="Enter label here"
          autoFocus
        />
      </FieldContainer>

      <Box display="flex" justify="flex-end">
        <SaveButton onClick={handleSubmit} disabled={!note.trim()}>
          Save
        </SaveButton>
      </Box>
    </PopoverContainer>
  );
};

export default NotePopover;
